import {signal, type Signal, type WritableSignal} from './arr-waveform-store';
export function fromEvent<K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  eventName: K
): WritableSignal<HTMLElementEventMap[K] | null> {
  const stream = signal<HTMLElementEventMap[K] | null>(null);
  const handler = (event: HTMLElementEventMap[K]) => {
    stream.set(event);
  };
  element.addEventListener(eventName, handler);
  (stream as any)._cleanup = () => {
    element.removeEventListener(eventName, handler);
  };
  return stream;
}
export function map<T, U>(
  source: Signal<T>,
  mapper: (value: T) => U
): Signal<U> {
  const result = signal<U>(mapper(source.value));
  const unsubscribe = source.subscribe((value) => {
    (result as WritableSignal<U>).set(mapper(value));
  });
  (result as any)._cleanup = unsubscribe;
  return result;
}
export function filter<T>(
  source: Signal<T>,
  predicate: (value: T) => boolean
): Signal<T | null> {
  const initialValue = predicate(source.value) ? source.value : null;
  const result = signal<T | null>(initialValue);
  const unsubscribe = source.subscribe((value) => {
    if (predicate(value)) {
      (result as WritableSignal<T | null>).set(value);
    } else {
      (result as WritableSignal<T | null>).set(null);
    }
  });
  (result as any)._cleanup = unsubscribe;
  return result;
}
export function debounce<T>(source: Signal<T>, delay: number): Signal<T> {
  const result = signal<T>(source.value);
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = source.subscribe((value) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      (result as WritableSignal<T>).set(value);
    }, delay);
  });
  (result as any)._cleanup = () => {
    clearTimeout(timeout);
    unsubscribe();
  };
  return result;
}
export function throttle<T>(source: Signal<T>, delay: number): Signal<T> {
  const result = signal<T>(source.value);
  let lastEmit = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = source.subscribe((value) => {
    const now = Date.now();
    const timeSinceLastEmit = now - lastEmit;
    if (timeSinceLastEmit >= delay) {
      (result as WritableSignal<T>).set(value);
      lastEmit = now;
    } else {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        (result as WritableSignal<T>).set(value);
        lastEmit = Date.now();
      }, delay - timeSinceLastEmit);
    }
  });
  (result as any)._cleanup = () => {
    clearTimeout(timeout);
    unsubscribe();
  };
  return result;
}
export function cleanup(stream: Signal<any>): void {
  const cleanupFn = (stream as any)._cleanup;
  if (typeof cleanupFn === 'function') {
    cleanupFn();
  }
}
